import React from 'react'
import { useRouter } from 'next/router'

import { LayoutWithHeader } from 'components/Layout'
import { useScreenSizeContext } from 'utils/hooks/context/useScreenSizeContext'
import DesktopLandingShell from './DesktopLandingShell'
import PelatihanDetailShell from './PelatihanDetailShell'
import { usePelatihanMandiriDesktopView } from '../hooks/usePelatihanMandiriDesktopView'
import { usePelatihanMandiriDesktopDetailView } from '../hooks/usePelatihanMandiriDesktopDetailView'
import { getModuleReturnUrl, getQueryLabel } from '../utils/detailNavigation'

import type { BreadcrumbItemDef } from 'components/ResponsiveLayout/Breadcrumb'

type LandingMenu = 'untuk-anda' | 'topik' | 'aksi-nyata' | 'webinar'

type ResponsiveErrorLayoutProps = {
  breadcrumbs?: BreadcrumbItemDef[]
  children: (isDesktop: boolean) => React.ReactNode
  landingMenu?: LandingMenu
  mobileOnBack?: () => void
}

const LANDING_MENUS: Record<string, LandingMenu> = {
  '/pelatihan-mandiri': 'untuk-anda',
  '/pelatihan-mandiri/daftar-topik': 'topik',
  '/pelatihan-mandiri/daftar-aksi-nyata': 'aksi-nyata',
  '/pelatihan-mandiri/webinar': 'webinar',
}

const getDetailBreadcrumbs = (
  pathname: string,
  query: ReturnType<typeof useRouter>['query']
): BreadcrumbItemDef[] => {
  const breadcrumbs: BreadcrumbItemDef[] = [
    { label: 'Pelatihan Mandiri', href: '/pelatihan-mandiri' },
  ]

  if (pathname === '/pelatihan-mandiri/webinar/[id]') {
    return [
      ...breadcrumbs,
      { label: 'Webinar', href: '/pelatihan-mandiri/webinar' },
      { label: 'Detail Webinar', href: null },
    ]
  }

  if (pathname.includes('/aksi-nyata/')) {
    return [
      ...breadcrumbs,
      {
        label: getQueryLabel(query.topik_name, 'Topik'),
        href: query.topik_id
          ? `/pelatihan-mandiri/topik/${query.topik_id}`
          : '/pelatihan-mandiri/daftar-aksi-nyata',
      },
      { label: 'Aksi Nyata', href: null },
    ]
  }

  if (query.topik || pathname.includes('/topik/')) {
    breadcrumbs.push({
      label: getQueryLabel(query.topik_name, 'Topik'),
      href: query.topik ? `/pelatihan-mandiri/topik/${query.topik}` : null,
    })
  }

  if (query.modul || pathname.includes('/modul/')) {
    breadcrumbs.push({
      label: getQueryLabel(query.modul_name ?? query.module_name, 'Modul'),
      href: getModuleReturnUrl(query),
    })
  }

  const pageLabel = pathname.includes('/quiz/')
    ? 'Latihan Pemahaman'
    : pathname.includes('/refleksi/')
    ? 'Cerita Reflektif'
    : pathname.includes('/post-test/passed')
    ? 'Hasil Post Test'
    : pathname.includes('/post-test')
    ? 'Post Test'
    : pathname.includes('/text/') || pathname.includes('/video/')
    ? getQueryLabel(query.materi_name, 'Materi')
    : pathname.includes('/topik/')
    ? null
    : 'Halaman'

  if (pageLabel) breadcrumbs.push({ label: pageLabel, href: null })
  return breadcrumbs
}

const ResponsiveErrorLayout = ({
  breadcrumbs,
  children,
  landingMenu,
  mobileOnBack,
}: ResponsiveErrorLayoutProps) => {
  const router = useRouter()
  const { isDesktop } = useScreenSizeContext()
  const isDesktopLandingEnabled = usePelatihanMandiriDesktopView()
  const isDesktopDetail = usePelatihanMandiriDesktopDetailView()
  const pathname = router.pathname ?? ''
  const routeMenu = landingMenu ?? LANDING_MENUS[pathname]
  const isPelatihanRoute = pathname.startsWith('/pelatihan-mandiri')
  const useDesktopLanding = Boolean(
    isPelatihanRoute && routeMenu && isDesktop && isDesktopLandingEnabled
  )
  const useDesktopDetail = isPelatihanRoute && isDesktopDetail
  const useDesktopShell = useDesktopLanding || useDesktopDetail
  const content = children(useDesktopShell)

  if (useDesktopLanding) {
    return (
      <DesktopLandingShell activeMenu={routeMenu}>
        <div
          data-testid="pelatihan-error-panel"
          className="mx-auto max-w-2xl rounded-lg border border-gray-20 bg-white p-8"
        >
          {content}
        </div>
      </DesktopLandingShell>
    )
  }

  if (useDesktopDetail) {
    return (
      <PelatihanDetailShell
        breadcrumbs={
          breadcrumbs ?? getDetailBreadcrumbs(pathname, router.query)
        }
        contentClassName="rounded-lg border border-gray-20 bg-white p-8"
      >
        <div
          data-testid="pelatihan-error-panel"
          className="mx-auto min-h-[320px] max-w-2xl"
        >
          {content}
        </div>
      </PelatihanDetailShell>
    )
  }

  return (
    <LayoutWithHeader title="" onClickBack={mobileOnBack}>
      {content}
    </LayoutWithHeader>
  )
}

export default ResponsiveErrorLayout
